import { queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import {
  dailyDashboardSchema,
  jobCardSchema,
  employeeReportSchema,
  type DailyDashboardItem,
  type JobCardItem,
  type EmployeeReport,
  type DailyDashboardParams,
  type JobCardParams,
  type EmployeeReportParams,
} from '../types/attendance.types';

export const attendanceQueryKeys = {
  all: () => ['attendance'] as const,
  dailyDashboard: (params: DailyDashboardParams) =>
    [...attendanceQueryKeys.all(), 'daily-dashboard', params] as const,
  jobCard: (params: JobCardParams) => [...attendanceQueryKeys.all(), 'job-card', params] as const,
  shiftJobCard: (params: JobCardParams) =>
    [...attendanceQueryKeys.all(), 'shift-job-card', params] as const,
  employeeReport: (params: EmployeeReportParams) =>
    [...attendanceQueryKeys.all(), 'employee-report', params] as const,
  shiftEmployeeReport: (params: EmployeeReportParams) =>
    [...attendanceQueryKeys.all(), 'shift-employee-report', params] as const,
};

export async function downloadDailyDashboard(params: DailyDashboardParams): Promise<void> {
  const res = await apiClient.get<Blob>(API.attendance.dailyDashboardExport, {
    params,
    responseType: 'blob',
  });

  downloadBlob(res.data, `daily-attendance-${params.date}.xlsx`);
}

export const dailyDashboardQueryOptions = (params: DailyDashboardParams) =>
  queryOptions({
    queryKey: attendanceQueryKeys.dailyDashboard(params),
    queryFn: async (): Promise<DailyDashboardItem[]> => {
      const res = await apiClient.get(API.attendance.dailyDashboard, { params });
      return dailyDashboardSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

function createJobCardDownload(endpoint: string, name: string) {
  return async (params: JobCardParams): Promise<void> => {
    const res = await apiClient.get<Blob>(endpoint, {
      params,
      responseType: 'blob',
    });

    downloadBlob(res.data, `${name}-${params.dateFrom}_${params.dateTo}.xlsx`);
  };
}

export const downloadJobCard = createJobCardDownload(API.attendance.jobCardExport, 'job-card');

export const downloadShiftJobCard = createJobCardDownload(
  API.attendance.shiftJobCardExport,
  'shift-job-card',
);

export type JobCardDownloadFn = typeof downloadJobCard;

function createJobCardQueryOptions(endpoint: string) {
  return (params: JobCardParams) =>
    queryOptions({
      queryKey: ['attendance', 'job-card', endpoint, params] as const,
      queryFn: async (): Promise<JobCardItem[]> => {
        const res = await apiClient.get(endpoint, { params });
        return jobCardSchema.parse(res.data);
      },
      staleTime: STALE_TIME,
    });
}

function createEmployeeReportQueryOptions(endpoint: string) {
  return (params: EmployeeReportParams) =>
    queryOptions({
      queryKey: ['attendance', 'employee-report', endpoint, params] as const,
      queryFn: async (): Promise<EmployeeReport> => {
        const res = await apiClient.get(endpoint, { params });
        return employeeReportSchema.parse(res.data);
      },
      staleTime: STALE_TIME,
    });
}

export const jobCardQueryOptions = createJobCardQueryOptions(API.attendance.jobCard);
export const shiftJobCardQueryOptions = createJobCardQueryOptions(API.attendance.shiftJobCard);

export const employeeReportQueryOptions = createEmployeeReportQueryOptions(
  API.attendance.employeeReport,
);
export const shiftEmployeeReportQueryOptions = createEmployeeReportQueryOptions(
  API.attendance.shiftEmployeeReport,
);

export type JobCardQueryOptionsFn = typeof jobCardQueryOptions;
export type EmployeeReportQueryOptionsFn = typeof employeeReportQueryOptions;
